// features/signatures/components/SignatureDetails.tsx
import { useEffect, useMemo, useState } from 'react'
import { MapPin, Smartphone, User, CheckCircle, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ImageMagnifier } from '@/components/ui/image-magnifier'
import { formatDate } from '@/components/form/contrato/contrato-form'

type Witness = {
  nome: string
  cpf: string
  maquina: string
  local: string
  assinatura: string
}

type Props = {
  ownerName: string
  ownerCpf: string
  isVerifed: boolean
  device: string
  createdAt: string
  location: string

  selfieSrc?: string
  signatureSrc?: string
  documentSrc: string
  witnesses?: Witness[]
}

export function SignatureDetails({
  ownerName,
  ownerCpf,
  isVerifed,
  device,
  createdAt,
  location,
  selfieSrc = '',
  signatureSrc = '',
  documentSrc,
  witnesses = []
}: Props) {
  const [leftImageOptions, setLeftImageOptions] = useState<{ value: string; label: string; src: string }[]>([])
  const [leftImage, setLeftImage] = useState<{ value: string; label: string; src: string } | null>(null)

  useEffect(() => {
    const options = [
      { value: 'selfie', label: 'Selfie com Documento', src: selfieSrc || '' },
      { value: 'signature', label: 'Assinatura', src: signatureSrc || '' }
    ].filter(o => !!o.src)
    setLeftImageOptions(options)
    setLeftImage(options[0] ?? null)
  }, [selfieSrc, signatureSrc])

  const documentImage = useMemo(() => ({ value: 'document', label: 'Documento', src: documentSrc }), [documentSrc])

  const handleLeftImageChange = (value: string) => {
    const sel = leftImageOptions.find(o => o.value === value)
    if (sel) setLeftImage(sel)
  }

  return (
    <>
      {/* Cabeçalho com status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle>Proprietário</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3">
              <User className="h-6 w-6 text-muted-foreground" />
              <p className="font-medium">{ownerName}</p>
            </div>
            <p className="text-sm text-muted-foreground ml-9">CPF: {ownerCpf}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle>Status da Verificação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3">
              {isVerifed ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-500" />
              )}
              <Badge
                className={`text-sm font-semibold rounded-full px-4 py-1 ${
                  isVerifed ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}
              >
                {isVerifed ? 'Verificado' : 'Pendente'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle>Aparelho</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3">
              <Smartphone className="h-6 w-6 text-muted-foreground" />
              <p className="font-medium">{device}</p>
            </div>
            <p className="text-sm text-muted-foreground ml-9">Assinado em: {formatDate(createdAt)}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle>Localização</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-muted-foreground" />
              <p className="font-medium">{location}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de comparação e testemunhas */}
      <Tabs defaultValue="verification" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="verification">Verificação de Documentos</TabsTrigger>
          {witnesses.length > 0 && <TabsTrigger value="witnesses">Testemunhas ({witnesses.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="verification" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Comparação de Documentos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-background p-4 rounded-lg">
                <label className="block text-sm font-semibold mb-2">Selecione uma imagem para comparar:</label>
                <Select value={leftImage?.value || ''} onValueChange={handleLeftImageChange}>
                  <SelectTrigger className="max-w-sm">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {leftImageOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="overflow-hidden">
                  <CardHeader>
                    <CardTitle>{leftImage?.label || 'Imagem'}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="aspect-square  bg-background rounded-lg overflow-hidden">
                      {leftImage?.src && (
                        <ImageMagnifier src={leftImage.src} alt={leftImage.label} className="w-full h-full" />
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <CardHeader>
                    <CardTitle>Documento</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="aspect-square  bg-background rounded-lg overflow-hidden">
                      <ImageMagnifier src={documentImage.src} alt="Documento" className="w-full h-full" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {witnesses.length > 0 && (
          <TabsContent value="witnesses" className="space-y-6">
            {witnesses.map((w, i) => (
              <Card key={i} className="overflow-hidden">
                <CardHeader>
                  <CardTitle>Testemunha {i + 1}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{w.nome}</p>
                          <p className="text-xs text-muted-foreground">Nome</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{w.cpf}</p>
                          <p className="text-xs text-muted-foreground">CPF</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                        <Smartphone className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{w.maquina || 'Não informado'}</p>
                          <p className="text-xs text-muted-foreground">Aparelho</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{w.local || 'Não informado'}</p>
                          <p className="text-xs text-muted-foreground">Localização</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold">Assinatura Digital</h4>
                      <div className="aspect-video bg-background rounded-lg overflow-hidden border">
                        {w.assinatura ? (
                          <ImageMagnifier src={w.assinatura} alt={`Assinatura ${w.nome}`} className="w-full h-full" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-muted-foreground">
                            Assinatura não disponível
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        )}
      </Tabs>
    </>
  )
}
